import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Checkbox, InputBase, Stack, Typography } from '@mui/material';

import { useFilterMenuTokens } from '../filterMenuTokens';
import type { MultiSelectFieldDef } from '../types';

interface MultiSelectEditorPanelProps {
  def: MultiSelectFieldDef;
  selected: string[];
  onToggle: (optionValue: string) => void;
  onClear: () => void;
  /** Explicit opens (click / keyboard / chip edit) move focus here; hover opens must not. */
  autoFocusSearch: boolean;
  /** ArrowLeft on an empty search — return focus to the root list. */
  onNavigateBack?: () => void;
}

/** The searchable checkbox list — the editor behind most filter types. */
export const MultiSelectEditorPanel: React.FC<MultiSelectEditorPanelProps> = ({
  def,
  selected,
  onToggle,
  onClear,
  autoFocusSearch,
  onNavigateBack,
}) => {
  const tokens = useFilterMenuTokens();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? def.options.filter(option => option.label.toLowerCase().includes(needle))
      : def.options;
  }, [def.options, query]);

  useEffect(() => {
    if (autoFocusSearch) inputRef.current?.focus();
  }, [autoFocusSearch]);

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && visible.length > 0) {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(previous => (previous + delta + visible.length) % visible.length);
      return;
    }
    if (event.key === 'Enter' && visible[activeIndex]) {
      event.preventDefault();
      onToggle(visible[activeIndex].value);
      return;
    }
    if (event.key === 'ArrowLeft' && query === '' && onNavigateBack) {
      event.preventDefault();
      onNavigateBack();
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          borderBottom: `1px solid ${tokens.line}`,
          height: 36,
          px: 1.5,
        }}
      >
        <SearchIcon sx={{ color: tokens.faint, fontSize: 16 }} />
        <InputBase
          fullWidth
          inputRef={inputRef}
          placeholder={def.searchPlaceholder ?? `Search ${def.label.toLowerCase()}…`}
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleSearchKeyDown}
          inputProps={{ 'aria-label': `Search ${def.label}` }}
          sx={{ fontSize: tokens.fontSize }}
        />
        {selected.length > 0 && (
          <Typography sx={{ color: tokens.faint, fontSize: tokens.metaFontSize }}>
            {selected.length}
          </Typography>
        )}
      </Stack>

      <Box sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
        {def.loading && (
          <Typography sx={{ color: tokens.dim, fontSize: tokens.fontSize, px: 1.5, py: 1 }}>
            Loading…
          </Typography>
        )}
        {!def.loading &&
          visible.map((option, index) => {
            const isSelected = selectedSet.has(option.value);
            return (
              <Stack
                direction="row"
                key={option.value}
                onClick={() => onToggle(option.value)}
                onMouseEnter={() => setActiveIndex(index)}
                sx={{
                  alignItems: 'center',
                  bgcolor: index === activeIndex ? tokens.hoverBg : undefined,
                  cursor: 'pointer',
                  gap: 1,
                  height: tokens.rowHeight,
                  px: 1.5,
                }}
              >
                <Box sx={{ alignItems: 'center', display: 'flex', width: 16 }}>
                  {/* Linear treatment: empty box appears on the hovered/active
                      row, becomes a checked box once selected. Visual-only —
                      the row is the click target. */}
                  {(isSelected || index === activeIndex) && (
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        color: tokens.faint,
                        p: 0,
                        pointerEvents: 'none',
                        '& .MuiSvgIcon-root': { fontSize: 16 },
                      }}
                      slotProps={{ input: { 'aria-hidden': true, tabIndex: -1 } }}
                    />
                  )}
                </Box>
                {option.icon && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      color: tokens.dim,
                      display: 'flex',
                      fontSize: 16,
                      width: 18,
                    }}
                  >
                    {option.icon}
                  </Box>
                )}
                <Typography
                  noWrap
                  sx={{
                    color: isSelected ? tokens.ink : tokens.dim,
                    fontSize: tokens.fontSize,
                  }}
                >
                  {option.label}
                </Typography>
              </Stack>
            );
          })}
        {!def.loading && visible.length === 0 && (
          <Typography sx={{ color: tokens.dim, fontSize: tokens.fontSize, px: 1.5, py: 2 }}>
            No matching options.
          </Typography>
        )}
      </Box>

      {selected.length > 0 && (
        <Box
          component="button"
          type="button"
          onClick={onClear}
          sx={{
            bgcolor: 'transparent',
            border: 'none',
            borderTop: `1px solid ${tokens.line}`,
            cursor: 'pointer',
            px: 1.5,
            py: 1,
            textAlign: 'left',
            width: '100%',
            '&:hover': { bgcolor: tokens.hoverBg },
          }}
        >
          <Typography sx={{ color: tokens.dim, fontSize: tokens.metaFontSize }}>
            Clear
          </Typography>
        </Box>
      )}
    </Box>
  );
};
